import React from 'react';
import { useTranslation } from 'react-i18next';
import FilterBox from '../../../../shared/components/filter-box/FilterBox';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import BotPlatformCard from '../BotPlatformCard';
import BotVisitTimeseriesChart from '../BotVisitTimeseriesChart';
import { getBotColor } from '../../../../shared/constants/botsActivityColors';
import { getPlatformIcon } from '../../../../shared/constants/botsActivityIcons';
import { BotsActivityTimeseriesResponse } from '../../../../shared/types/bots-activity/BotsActivityTimeseriesResponse';

type Granularity = 'hour' | 'day' | 'week';
type RangeOption = '1d' | '7d' | '30d' | '90d' | '365d';

const GRANULARITY_OPTIONS = [
  { key: 'hour' as const, labelKey: 'hourlyFilter' as const },
  { key: 'day' as const, labelKey: 'dailyFilter' as const },
  { key: 'week' as const, labelKey: 'weeklyFilter' as const },
];

interface FeaturedPlatform {
  platform: string;
  current: number;
  previous: number;
  trendPct: number | null;
}

interface BotsActivityVisitsPanelProps {
  granularity: Granularity;
  onGranularityChange: (g: Granularity) => void;
  featuredPlatforms: FeaturedPlatform[];
  timeseries: BotsActivityTimeseriesResponse | undefined;
  hasNoActivityData: boolean;
  activeRange: RangeOption;
}

export const BotsActivityVisitsPanel: React.FC<BotsActivityVisitsPanelProps> = ({
  granularity,
  onGranularityChange,
  featuredPlatforms,
  timeseries,
  hasNoActivityData,
  activeRange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('botsActivity.crawlActivity')}
          <CustomTooltip tooltipText="botsActivity.botsActivityHint" />
        </div>
        <FilterBox
          items={GRANULARITY_OPTIONS.map((g) => ({
            label: t(g.labelKey),
            isActive: granularity === g.key,
            onClick: () => onGranularityChange(g.key),
          }))}
        />
      </div>

      <div className="bots-activity__activity-card">
        <div className="bots-activity__platform-cards">
          {featuredPlatforms.map((p) => (
            <BotPlatformCard
              key={p.platform}
              platform={p.platform}
              value={p.current}
              trend={p.trendPct}
              color={getBotColor(p.platform)}
              iconClass={getPlatformIcon(p.platform)}
            />
          ))}
        </div>

        <div className="bots-activity__chart-wrap">
          {hasNoActivityData ? (
            <EmptyPanel textKey="botsActivity.noActivityYet" size="l" />
          ) : (
            <BotVisitTimeseriesChart
              data={timeseries}
              granularity={granularity}
              range={activeRange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BotsActivityVisitsPanel;
